import { useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

function generate(length, opts) {
  var chars = '';
  if (opts.lower) chars += 'abcdefghijklmnopqrstuvwxyz';
  if (opts.upper) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (opts.numbers) chars += '0123456789';
  if (opts.symbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';
  if (chars === '') chars = 'abcdefghijklmnopqrstuvwxyz';
  var arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  var result = '';
  for (var i = 0; i < length; i++) result += chars[arr[i] % chars.length];
  return result;
}

function getStrength(pw) {
  var score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  if (score <= 2) return { label: 'Weak', color: '#ff6b6b' };
  if (score <= 4) return { label: 'Medium', color: '#ffa94d' };
  return { label: 'Strong', color: '#00c9a7' };
}

function PasswordGenerator() {
  const [length, setLength] = useState(16);
  const [opts, setOpts] = useState({ lower: true, upper: true, numbers: true, symbols: true });
  const [passwords, setPasswords] = useState([]);
  const [copied, setCopied] = useState(-1);

  var gen = useCallback(function () {
    var list = [];
    for (var i = 0; i < 5; i++) list.push(generate(length, opts));
    setPasswords(list);
  }, [length, opts]);

  var copy = function (text, idx) {
    navigator.clipboard.writeText(text).then(function () { setCopied(idx); setTimeout(function () { setCopied(-1); }, 2000); });
  };

  var toggleOpt = function (key) { setOpts(Object.assign({}, opts, { [key]: !opts[key] })); };

  return (
    <div className="page-prettifier">
      <SEO title="Password Generator - Strong Random Passwords" description="Free online password generator. Create strong, random, and secure passwords instantly. Customize length, characters, and symbols." path="/password-generator" />
      <div className="container">
        <div className="tool-header">
          <h1>Password Generator</h1>
          <p>Generate strong, random passwords that are hard to crack. Uses cryptographically secure randomness.</p>
        </div>
        <AdUnit />
        <div className="toolbar" style={{ flexWrap: 'wrap' }}>
          <div className="toolbar-left">
            <label htmlFor="pw-length" className="indent-label">Length:</label>
            <input id="pw-length" type="number" min="4" max="128" value={length} onChange={function (e) { setLength(parseInt(e.target.value) || 8); }} className="indent-select" style={{ width: '65px' }} />
          </div>
          <div className="toolbar-left" style={{ gap: '0.75rem' }}>
            <label className="checkbox-label"><input type="checkbox" checked={opts.lower} onChange={function () { toggleOpt('lower'); }} /> a-z</label>
            <label className="checkbox-label"><input type="checkbox" checked={opts.upper} onChange={function () { toggleOpt('upper'); }} /> A-Z</label>
            <label className="checkbox-label"><input type="checkbox" checked={opts.numbers} onChange={function () { toggleOpt('numbers'); }} /> 0-9</label>
            <label className="checkbox-label"><input type="checkbox" checked={opts.symbols} onChange={function () { toggleOpt('symbols'); }} /> Symbols</label>
          </div>
          <div className="toolbar-right">
            <button onClick={gen} className="btn btn-primary" type="button">Generate</button>
          </div>
        </div>
        <div className="password-list">
          {passwords.map(function (pw, i) {
            var strength = getStrength(pw);
            return (
              <div key={i} className="password-row">
                <code className="password-text">{pw}</code>
                <span className="password-strength" style={{ color: strength.color }}>{strength.label}</span>
                <button onClick={function () { copy(pw, i); }} className="copy-btn" type="button">{copied === i ? '✓' : 'Copy'}</button>
              </div>
            );
          })}
          {passwords.length === 0 && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Click Generate to create passwords</p>}
        </div>
        <section className="tool-guide">
          <h2>Why Strong Passwords Matter</h2>
          <p>Weak passwords are the number one cause of account breaches. A password like "password123" can be cracked in seconds. A strong 16-character password with mixed characters would take billions of years to crack with current technology. Use a unique password for every account and store them in a password manager.</p>
          <h2>Password Security Tips</h2>
          <ul>
            <li>Use at least 12 characters — longer is always better.</li>
            <li>Include uppercase, lowercase, numbers, and symbols.</li>
            <li>Never reuse passwords across different accounts.</li>
            <li>Use a password manager to store and autofill your passwords.</li>
            <li>Enable two-factor authentication (2FA) wherever possible.</li>
            <li>Change passwords immediately if a service reports a data breach.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default PasswordGenerator;
